const GUEST_WISHLIST_KEY = 'cb-wishlist';

function getGuestWishlist() {
  try {
    return JSON.parse(localStorage.getItem(GUEST_WISHLIST_KEY)) || [];
  } catch {
    return [];
  }
}

function setGuestWishlist(items) {
  localStorage.setItem(GUEST_WISHLIST_KEY, JSON.stringify(items));
}

class WishlistButton extends HTMLElement {
  constructor() {
    super();
    this.controller = new AbortController();
    this.isWishlisted = false;
    this.toggling = false;

    this.iconOutline = null;
    this.iconFilled = null;
    this.button = null;
    this.productId = null;
    this.customerId = null;
    this.productHandle = null;
    this.apiUrl = null;
  }

  connectedCallback() {
    const signal = this.controller.signal;

    this.productId = this.getAttribute('data-product-id');
    this.customerId = this.getAttribute('data-customer-id');
    this.productHandle = this.getAttribute('data-product-handle');
    this.videoUrl = this.getAttribute('data-video-url');
    this.apiUrl = window.__cbApiUrl;

    this.iconOutline = this.querySelector('[data-wishlist-icon-outline]');
    this.iconFilled = this.querySelector('[data-wishlist-icon-filled]');
    this.button = this.querySelector('button');
    this.textUnselected = this.querySelector('[data-wishlist-add-text]');
    this.textSelected = this.querySelector('[data-wishlist-added-text]');

    // Determine initial state from server data (logged-in) and localStorage (guest)
    const dataEl = document.getElementById('wishlist-data');
    if (dataEl) {
      try {
        const wishlistIds = JSON.parse(dataEl.textContent);
        this.isWishlisted = wishlistIds.includes(Number(this.productId));
      } catch (e) {
        console.error('wishlist-button: could not parse wishlist data', e);
      }
    }

    // Also check guest localStorage
    if (!this.isWishlisted) {
      const guestItems = getGuestWishlist();
      this.isWishlisted = guestItems.some(
        (item) => String(item.id) === String(this.productId)
      );
    }

    this.syncUI();

    // Always attach click listener (guest or logged-in)
    if (this.button) {
      this.button.addEventListener(
        'click',
        () => {
          this.toggle();
          if (String(this.isWishlisted) === 'true') {
            setTimeout(() => {
              this.iconFilled.classList.remove('group-hover:rotate-90');
            }, 300);
            this.iconFilled.addEventListener(
              'mouseleave',
              () => {
                this.iconFilled.classList.add('group-hover:rotate-90');
              },
              { once: true, signal }
            );
          } else {
            setTimeout(() => {
              this.iconOutline.classList.remove('group-hover:rotate-90');
            }, 300);
            this.iconOutline.addEventListener(
              'mouseleave',
              () => {
                this.iconOutline.classList.add('group-hover:rotate-90');
              },
              { once: true, signal }
            );
          }
        },
        { signal }
      );
    }

    // Sync across instances on same page
    document.addEventListener(
      'wishlist:updated',
      (e) => {
        if (String(e.detail.productId) === String(this.productId)) {
          this.isWishlisted = e.detail.isWishlisted;
          this.syncUI();
        }
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.controller.abort();
  }

  syncUI() {
    if (this.iconOutline) {
      this.iconOutline.classList.toggle('hidden', this.isWishlisted);
      if (this.textUnselected) {
        this.textUnselected.classList.toggle('opacity-0', this.isWishlisted);
      }
    }
    if (this.iconFilled) {
      this.iconFilled.classList.toggle('hidden', !this.isWishlisted);
      if (this.textSelected) {
        this.textSelected.classList.toggle('opacity-0', !this.isWishlisted);
      }
    }
    if (this.button) {
      this.button.setAttribute('aria-pressed', String(this.isWishlisted));
    }
  }

  async toggle() {
    if (this.toggling) return;

    // Guest mode — toggle in localStorage
    if (!this.customerId) {
      if (!this.productId) return;
      this.toggling = true;

      this.isWishlisted = !this.isWishlisted;
      this.syncUI();

      const guestItems = getGuestWishlist();
      const idx = guestItems.findIndex(
        (item) => String(item.id) === String(this.productId)
      );

      if (idx > -1) {
        guestItems.splice(idx, 1);
      } else {
        const entry = {
          id: Number(this.productId),
          h: this.productHandle || '',
        };
        const colorCount = this.getAttribute('data-color-count');
        if (colorCount) entry.cc = Number(colorCount);
        if (this.videoUrl) entry.v = this.videoUrl;
        guestItems.push(entry);
      }

      setGuestWishlist(guestItems);

      document.dispatchEvent(
        new CustomEvent('wishlist:updated', {
          detail: {
            productId: this.productId,
            isWishlisted: this.isWishlisted,
          },
        })
      );

      this.toggling = false;

      return;
    }

    // Logged-in mode — call API
    if (!this.productId) return;
    this.toggling = true;

    // Optimistic UI update
    this.isWishlisted = !this.isWishlisted;
    this.syncUI();

    try {
      const url = `${this.apiUrl}/wishlist/toggle?customerId=${this.customerId}&productId=${this.productId}`;
      const response = await fetch(url, { method: 'POST' });
      const data = await response.json();

      if (!response.ok) {
        // Revert on error
        this.isWishlisted = !this.isWishlisted;
        this.syncUI();
        return;
      }

      // Dispatch for cross-component sync (other instances won't re-call API)
      document.dispatchEvent(
        new CustomEvent('wishlist:updated', {
          detail: {
            productId: this.productId,
            isWishlisted: this.isWishlisted,
          },
        })
      );
    } catch (error) {
      console.error('wishlist-button: toggle error', error);
      // Revert on error
      this.isWishlisted = !this.isWishlisted;
      this.syncUI();
    } finally {
      this.toggling = false;
    }
  }
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}
